import React, { createRef, useState } from "react";
import styles from "./AddGroup.module.css";
import { createGroup, Group } from "../../../user/User";
import PuiTextbox from "../../../components/elements/PuiTextbox";
import PuiTypography from "../../../components/elements/PuiTypography";
import PuiButton from "../../../components/elements/PuiButton";
import { countCharLength, validateRequired } from "../../../utils/ValidationUtils";
import {
    MSG_INPUT_MISS_REQUIRED,
    MSG_INPUT_OVER_MAXLENGTH,
    MSG_INPUT_REQUEST_INPUT,
} from "../../../utils/Messages";
import { setMessageArgs } from "../../../utils/LogicUtils";
import { isEmpty } from "../../../utils/ObjectUtils";
import { createTheme, ThemeProvider } from "@material-ui/core";

interface AddGroupProps {
    // クロースイベント
    onClose?: (item: Group) => void;
}

const theme = createTheme({
    palette: {
        secondary: {
            main: "#12e07e",
        },
    },
});

export default function AddGroup({ onClose }: AddGroupProps) {
    // 定数
    const MAXLEN_TITLE = 40;
    const MAXLEN_DESCRIPTION = 400;

    // パラメータ初期化
    const titleRef = createRef<HTMLInputElement>();
    const descriptionRef = createRef<HTMLInputElement>();
    const [validInput, setValidInput] = useState(true);
    const [titleError, setTitleError] = useState("");
    const [descriptionError, setDescriptionError] = useState("");

    // 入力チェック（必須、桁数）
    const checkInput = () => {
        if (
            validateRequired(titleRef.current?.value, descriptionRef.current?.value) &&
            countCharLength(titleRef.current?.value) <= MAXLEN_TITLE &&
            countCharLength(descriptionRef.current?.value) <= MAXLEN_DESCRIPTION
        ) {
            setValidInput(true);
            setTitleError("");
            setDescriptionError("");
            return true;
        } else {
            setValidInput(false);
            return false;
        }
    };

    // タイトル編集イベント
    const handleInputTitle = () => {
        checkInput();
        countCharLength(titleRef.current?.value) > MAXLEN_TITLE
            ? setTitleError(MSG_INPUT_OVER_MAXLENGTH)
            : setTitleError("");
    };

    // 概要編集イベント
    const handleInputDescription = () => {
        checkInput();
        countCharLength(descriptionRef.current?.value) > MAXLEN_DESCRIPTION
            ? setDescriptionError(MSG_INPUT_OVER_MAXLENGTH)
            : setDescriptionError("");
    };

    // Group追加ボタン押下イベント
    const handleCreateProject = () => {
        let result = checkInput();

        // タイトルに入力エラーがある場合フォーカスする
        if (
            !validateRequired(titleRef.current?.value) ||
            countCharLength(titleRef.current?.value) > MAXLEN_TITLE
        ) {
            titleRef.current?.focus();
            return;
        }

        // 概要に入力エラーがある場合フォーカスする
        if (
            !validateRequired(descriptionRef.current?.value) ||
            countCharLength(descriptionRef.current?.value) > MAXLEN_DESCRIPTION
        ) {
            descriptionRef.current?.focus();
            return;
        }

        // 新たなグループ情報を作成する
        if (result && onClose) {
            onClose(
                createGroup(
                    String(Date.now()),
                    String(titleRef.current?.value),
                    [],
                    String(descriptionRef.current?.value),
                    [],
                    [],
                    []
                )
            );
        }
    };

    return (
        <div className={styles.root}>
            <ThemeProvider theme={theme}>
                <div className={styles.title}>
                    <PuiTextbox
                        id="title"
                        ref={titleRef}
                        placeholder={setMessageArgs(MSG_INPUT_REQUEST_INPUT, "Group名")}
                        error={!isEmpty(titleError)}
                        helperText={setMessageArgs(titleError, "20", "40")}
                        onChange={handleInputTitle}
                    />
                </div>
                <div className={styles.description}>
                    <PuiTextbox
                        id="description"
                        ref={descriptionRef}
                        placeholder={setMessageArgs(MSG_INPUT_REQUEST_INPUT, "Group概要")}
                        multiline={true}
                        rows={5}
                        error={!isEmpty(descriptionError)}
                        helperText={setMessageArgs(descriptionError, "200", "400")}
                        onChange={handleInputDescription}
                    />
                </div>
            </ThemeProvider>
            {!validInput && (
                <div className={styles.error}>
                    <PuiTypography size={14} color="red">
                        {MSG_INPUT_MISS_REQUIRED}
                    </PuiTypography>
                </div>
            )}
            <div className={styles.buttonRow}>
                <div className={styles.createButton}>
                    <PuiButton children={"Group追加"} size="medium" onClick={handleCreateProject} />
                </div>
            </div>
        </div>
    );
}
